"use client";

import { useCallback, useEffect, useState } from "react";

const MAX_GUEST_SEARCH_COUNT = 5;

interface GuestSearchLimitState {
  remainingCount: number;
  maxCount: number;
  isLimitReached: boolean;
  isLoading: boolean;
  consumeSearch: () => Promise<boolean>;
  refreshCount: () => Promise<void>;
}

export function useGuestSearchLimit(): GuestSearchLimitState {
  const [remainingCount, setRemainingCount] = useState(MAX_GUEST_SEARCH_COUNT);
  const [isLoading, setIsLoading] = useState(true);

  const refreshCount = useCallback(async () => {
    try {
      const response = await fetch("/api/search/usage");
      if (response.ok) {
        const data = await response.json();
        setRemainingCount(data.remainingCount);
      }
    } catch (error) {
      console.error("Failed to fetch search usage:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  const consumeSearch = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch("/api/search/usage/consume", {
        method: "POST",
      });

      const data = await response.json();

      if (response.ok) {
        if (data.remainingCount >= 0) {
          setRemainingCount(data.remainingCount);
        }
        return true;
      }

      if (response.status === 403) {
        setRemainingCount(0);
        return false;
      }

      return false;
    } catch (error) {
      console.error("Failed to consume search:", error);
      return false;
    }
  }, []);

  return {
    remainingCount,
    maxCount: MAX_GUEST_SEARCH_COUNT,
    isLimitReached: remainingCount <= 0,
    isLoading,
    consumeSearch,
    refreshCount,
  };
}
